// 会议纪要 (office/notes.ts): in a 会议室 the toolbar's 记录 starts recording the room (anyone there can stop it; it also
// stops by itself once the room has stayed empty), the header says 「正在记录」, and everyone in the room (and whoever
// walks in later) gets a short notice. When it stops, the notes come in four parts (总结, 待办, 关键决策, 章节), each shown
// as soon as it's written; a part that failed has 重试. Every 待办 / 决策 / 要点 shows how many of the meeting's own
// sentences back it (原话 N 句) and opens them, with who said them and when. 纪要 lists the meetings I was in.
// Only participants see any of it (the office checks).
import { icon, put } from '/icons.js';
import { t, tr, tParts, has, errorText, onLang, fmtTime, fmtDate } from '/i18n.js';

/** Can the Deck app post / DM on Raft (a raftBot status, or a DM's no_handle / other_server), in words: notes.bot.<status>.
 *  `app` is the app's Raft handle; a channel the app isn't in is worded with the channel. */
const botText = (s, app = 'raft-deck', channel) => (s === 'not_member' && channel ? t('notes.bot.notMemberOf', { channel, app })
  : t(has(`notes.bot.${s}`) ? `notes.bot.${s}` : 'notes.bot.failed', { app }));
/** A refusal from office/notes.ts (notes_err, notes_posted): its code in words, else the server's message. */
const errText = (m) => (m.code === 'E_BOT' ? botText(m.params?.status, m.params?.app, m.params?.channel)
  : errorText('notes.err', { code: m.code, params: m.params, message: m.reason }));

/** The four parts, in order: [part, its heading's catalog key]. */
const PARTS = [['summary', 'notes.part.summary'], ['todos', 'notes.part.todos'], ['decisions', 'notes.part.decisions'], ['chapters', 'notes.part.chapters']];
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const hm = (at) => fmtTime(at);
const day = (at) => fmtDate(at, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
/** A line's speaker: 「X：」 (typed: 「X（打字）：」) */
const saidBy = (name, typed) => t(typed ? 'notes.saidTyped' : 'notes.said', { name });
/** Names in a sentence: A、B */
const names = (list) => list.join(t('notes.sep'));

const CSS = `
#notesPanel{position:fixed;inset:0;z-index:55;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,.45);font:14px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif)}
#notesPanel.on{display:flex}
#notesPanel .nbox{width:min(680px,calc(100% - 16px));height:min(86vh,760px);display:flex;flex-direction:column;background:#262a33;color:#f4efe4;border:3px solid #1b1410;border-radius:14px;overflow:hidden;box-shadow:0 10px 40px rgba(0,0,0,.5)}
#notesPanel .nh{display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #3a3f4b;font-weight:700}
#notesPanel .nh .sp{flex:1} #notesPanel button{font:inherit;cursor:pointer}
#notesPanel .nh button{border:0;background:transparent;color:#f4efe4;padding:2px 6px;display:inline-flex;align-items:center;gap:4px}
#notesPanel .nb{flex:1;overflow:auto;padding:10px 14px}
#notesPanel .empty{color:#8b8579;padding:20px 0;text-align:center}
#notesPanel .item{display:flex;align-items:center;gap:8px;width:100%;text-align:left;border:0;border-radius:10px;background:#1c1f26;color:#f4efe4;padding:10px 12px;margin:0 0 8px}
#notesPanel .item:hover{background:#2d323c} #notesPanel .item b{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#notesPanel .meta{color:#b9b2a5;font-size:12px}
#notesPanel .tag{font-size:11px;border-radius:999px;padding:0 8px;background:#3a3f4b;color:#f4efe4;white-space:nowrap}
#notesPanel .tag.rec{background:#dc2626;color:#fff} #notesPanel .tag.gen{background:#FDD03B;color:#1b1410}
#notesPanel h3{font-size:15px;margin:16px 0 6px;display:flex;align-items:center;gap:8px}
#notesPanel .wait{color:#c9c4ba;font-size:13px} #notesPanel .bad{color:#fca5a5;font-size:13px}
#notesPanel .bad button,#notesPanel .cite{border:2px solid #3a3f4b;border-radius:999px;background:transparent;color:#f4efe4;font-size:12px;padding:0 8px;margin-left:6px}
#notesPanel .cite{border-color:#4f9dde;color:#9cc9f5} #notesPanel .cite.on{background:#4f9dde;color:#10151d}
#notesPanel ul{margin:0;padding-left:18px} #notesPanel li{margin:4px 0}
#notesPanel .who{color:#FDD03B;font-size:12px;margin-left:6px}
#notesPanel .quotes{margin:4px 0 6px;padding:6px 8px;border-left:3px solid #4f9dde;background:#1c1f26;border-radius:6px;font-size:13px}
#notesPanel .quotes div,#notesPanel .tr div{margin:2px 0} #notesPanel .q .n,#notesPanel .tr .n{color:#FDD03B;margin-right:4px} #notesPanel .q .t,#notesPanel .tr .t{color:#8b8579;font-size:11px;margin-right:4px}
#notesPanel .tr div.hl{background:#3a3520}
#notesPanel .chap{border:0;background:transparent;color:#9cc9f5;padding:0;text-align:left}
#notesPanel .note{color:#8b8579;font-size:12px;margin-top:4px}
#notesPanel .tr{font-size:13px}
#notesPanel .raft{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:8px;padding:8px;border-radius:10px;background:#1c1f26;font-size:13px}
#notesPanel .raft input{font:inherit;width:140px;padding:3px 8px;border:0;border-radius:8px;background:#fff8e7;color:#1b1410}
#notesPanel .raft button{border:0;border-radius:8px;background:#FDD03B;color:#1b1410;padding:3px 10px;font-size:13px}
#notesPanel .raft button:disabled{opacity:.5;cursor:default} #notesPanel .raft .ok{color:#86efac}
#notesPanel .raft.col{flex-direction:column;align-items:flex-start;gap:4px} #notesPanel .raft .bad{color:#fca5a5} #notesPanel .raft .wait{color:#c9c4ba}
#notesStart{position:fixed;inset:0;z-index:56;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,.45);font:14px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif)}
#notesStart.on{display:flex}
#notesStart .nbox{width:min(400px,calc(100% - 24px));background:#262a33;color:#f4efe4;border:3px solid #1b1410;border-radius:14px;padding:14px 16px;box-shadow:0 10px 40px rgba(0,0,0,.5)}
#notesStart b{display:block;font-size:15px;margin-bottom:6px} #notesStart p{margin:0 0 8px;color:#c9c4ba;font-size:13px}
#notesStart label{display:flex;gap:8px;align-items:flex-start;margin:8px 0;cursor:pointer} #notesStart input{margin-top:3px}
#notesStart .st{font-size:12px;margin:0 0 10px 24px;color:#c9c4ba} #notesStart .st.bad{color:#fca5a5} #notesStart .st.ok{color:#86efac}
#notesStart .row{display:flex;gap:8px;justify-content:flex-end}
#notesStart button{font:inherit;border:0;border-radius:8px;padding:5px 14px;cursor:pointer} #notesStart .go{background:#FDD03B;color:#1b1410;font-weight:700} #notesStart .no{background:#3a3f4b;color:#f4efe4}
#meet .mrec{display:none;align-items:center;gap:5px;background:#dc2626;color:#fff;border-radius:999px;padding:1px 9px;font-size:12px;white-space:nowrap}
#meet .mrec.on{display:inline-flex} #meet .mrec i{width:8px;height:8px;border-radius:50%;background:#fff;animation:nrec 1.4s infinite}
#meet .mrec.paused{background:#854d0e} #meet .mrec.paused i{animation:none}
@keyframes nrec{50%{opacity:.25}}
#meet .mbar button.rec{background:#7f1d1d}
@media (max-width:640px){#notesPanel .nbox{height:calc(100% - 16px)} #meet .mrec{font-size:0;padding:4px 6px}
  /* record and notes make the phone's toolbar too long for one screen: it scrolls sideways, leave stays at the right */
  body #meet .mbar{overflow-x:auto;justify-content:flex-start;scrollbar-width:none} body #meet .mbar::-webkit-scrollbar{display:none}
  body #meet .mbar button{flex:none;min-width:48px;white-space:nowrap} body #meet .mbar .leave{position:sticky;right:0;box-shadow:-8px 0 8px #1c1f26}}`;

/**
 * @param {object} o
 * @param {(m) => void} o.send  @param {(text, go?, icon?, goText?) => void} o.toast
 * @param {() => string|null} o.room the 会议室 I'm in (zone id), meeting.js room()
 * @param {boolean} [o.guest] I'm a guest: I can't start a recording (only stop one)
 */
export function initNotes(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  let live = {};            // zone -> {id, by, at}: rooms recording now (everyone is told)
  let unavailable = new Set(), stopped = {};
  const stopTold = new Set();
  let supported = false;    // the office knows 会议纪要 (its welcome had notesLive): else no buttons
  let supportsDraft = false; // old office instances don't generate live drafts
  let told = new Set();     // recording ids I've had the notice for
  let toasted = new Set();  // recording ids whose 「纪要整理好了」 I've seen
  let view = null;          // null | 'list' | id (the notes open)
  let full = null, lines = [], openCites = new Set(), hl = null;
  let posting = null, postChannel = null; // the notes being posted to Raft now (id), and the channel typed
  let raft = null; // {status, text, handle}: can the Deck app post on my Raft server (office/notes.ts notes_raft_check)
  const checkRaft = () => { if (!o.guest) o.send({ t: 'notes_raft_check' }); };

  // 开始记录: what happens, and 自动发给参会的人 (on unless unticked), with whether Raft lets the app send now
  const sb = el('div'); sb.id = 'notesStart';
  const sbox = el('div', 'nbox'), sAuto = el('input'), sSt = el('div', 'st'), sWhat = el('p'), sGo = tr(el('button', 'go'), 'notes.start.go'), sNo = tr(el('button', 'no'), 'notes.start.cancel');
  sAuto.type = 'checkbox'; sAuto.checked = true;
  const sLab = el('label'); sLab.append(sAuto, tr(el('span'), 'notes.start.auto'));
  const sRow = el('div', 'row'); sRow.append(sNo, sGo);
  sbox.append(tr(el('b'), 'notes.start.title'), sWhat, sLab, sSt, sRow);
  sb.append(sbox); document.body.append(sb);
  sb.addEventListener('click', (e) => { if (e.target === sb) sb.classList.remove('on'); });
  sb.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Escape') sb.classList.remove('on'); });
  sNo.onclick = () => sb.classList.remove('on');
  sGo.onclick = () => { sb.classList.remove('on'); o.send({ t: 'notes_start', auto: sAuto.checked, draft: supportsDraft }); };
  function startWhat() { sWhat.textContent = t(supportsDraft ? 'notes.start.whatDraft' : 'notes.start.what'); }
  startWhat();
  function startStatus() {
    const s = !raft ? null : raft.status === 'ready' && !raft.handle ? { ok: false, text: t('notes.start.noHandle') } : { ok: raft.status === 'ready', text: botText(raft.status, raft.app) };
    sSt.className = 'st' + (s ? (s.ok ? ' ok' : ' bad') : ''); sSt.textContent = s ? s.text : t('notes.start.checking');
  }

  const root = el('div'); root.id = 'notesPanel';
  const box = el('div', 'nbox'), head = el('div', 'nh'), back = el('button'), title = el('span', '', t('notes.title')), x = el('button');
  back.append(icon('undo'), tr(el('span'), 'notes.all')); x.append(icon('close')); tr(x, null, null, { title: 'notes.close' });
  back.onclick = () => openList(); x.onclick = () => close();
  head.append(back, title, el('span', 'sp'), x);
  const body = el('div', 'nb');
  box.append(head, body); root.append(box); document.body.append(root);
  root.addEventListener('click', (e) => { if (e.target === root) close(); });
  root.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Escape') close(); });
  for (const t of ['keyup', 'keypress']) root.addEventListener(t, (e) => e.stopPropagation());

  // another language (设置 → 语言): what's open draws itself again
  onLang(() => {
    startWhat();
    if (view === 'list') { title.textContent = t('notes.title'); if (listed) renderList(listed); }
    else if (full) renderFull();
    if (sb.classList.contains('on') && (raft || !sSt.classList.contains('bad'))) startStatus(); // (not over the 8 s 「查不到」)
  });
  function close() { view = null; full = null; root.classList.remove('on'); }
  function openList() { view = 'list'; full = null; listed = null; root.classList.add('on'); back.style.display = 'none'; title.textContent = t('notes.title'); body.replaceChildren(el('div', 'empty', t('notes.loading'))); o.send({ t: 'notes_list' }); }
  function openOne(id) { if (!raft) checkRaft(); view = id; root.classList.add('on'); back.style.display = ''; body.replaceChildren(el('div', 'empty', t('notes.loading'))); o.send({ t: 'notes_get', id }); }

  let listed = null; // the list as it came last (to draw it again in another language)
  function renderList(items) {
    if (view !== 'list') return;
    listed = items;
    if (!items.length) { body.replaceChildren(el('div', 'empty', t('notes.list.empty'))); return; }
    body.replaceChildren(...items.map((it) => {
      const b = el('button', 'item'); b.onclick = () => openOne(it.id);
      const st = it.state === 'rec' ? ['rec', t('notes.list.rec')] : it.state === 'gen' ? ['gen', t('notes.list.gen')] : ['', t('notes.list.done', { n: Object.values(it.parts).filter((s) => s === 'ok').length })];
      b.append(put(el('b'), 'note', it.room), el('span', 'meta', day(it.at)), el('span', 'tag ' + st[0], st[1]));
      return b;
    }));
  }

  // ---- one meeting's notes
  const lineOf = (n) => lines[n - 1];
  function quotes(src) {
    const q = el('div', 'quotes');
    for (const n of src) { const l = lineOf(n); if (!l) continue; const d = el('div', 'q'); d.append(el('span', 't', hm(l.at)), el('span', 'n', saidBy(l.name)), l.text); q.append(d); }
    return q;
  }
  /** An item with its 原话 N 句 (opens the quotes under it). */
  function cited(li, it, key) {
    const c = el('button', 'cite' + (openCites.has(key) ? ' on' : ''), t('notes.cite', { n: it.src.length }));
    c.title = t('notes.citeTip');
    c.onclick = () => { openCites.has(key) ? openCites.delete(key) : openCites.add(key); renderFull(); };
    li.append(c);
    if (openCites.has(key)) li.append(quotes(it.src));
  }
  function section(part, name) {
    const h = el('h3', '', name), s = full.parts[part], out = [h];
    if (!s || s.st === 'wait') { out.push(el('div', 'wait', t(full.state === 'rec' ? 'notes.part.later' : 'notes.part.wait'))); return out; }
    if (s.st === 'err') {
      const d = el('div', 'bad', t(s.err === 'not_configured' ? 'notes.part.notConfigured' : 'notes.part.failed'));
      if (s.err !== 'not_configured') { const r = el('button', '', t('notes.part.retry')); r.onclick = () => { o.send({ t: 'notes_retry', id: full.id, part }); }; d.append(r); }
      out.push(d); return out;
    }
    const d = s.data, ul = el('ul');
    if (part === 'summary') {
      out.push(el('div', '', d.summary));
      d.points.forEach((p, i) => { const li = el('li', '', p.text); cited(li, p, `p${i}`); ul.append(li); });
      if (d.points.length) out.push(ul);
    } else if (part === 'todos' || part === 'decisions') {
      const list = d[part];
      if (!list.length) out.push(el('div', 'wait', t(part === 'todos' ? 'notes.part.noTodos' : 'notes.part.noDecisions')));
      list.forEach((it, i) => {
        const li = el('li', '', it.text);
        if (part === 'todos' && (it.owner || it.due)) li.append(el('span', 'who', [it.owner, it.due].filter(Boolean).join(' · ')));
        cited(li, it, `${part[0]}${i}`); ul.append(li);
      });
      if (list.length) out.push(ul);
    } else {
      d.chapters.forEach((c) => {
        const li = el('li'), b = el('button', 'chap', `${hm(lineOf(c.from)?.at ?? full.at)} ${c.title}`);
        b.onclick = () => { hl = [c.from, c.to]; renderFull(); body.querySelector(`[data-n="${c.from}"]`)?.scrollIntoView({ block: 'center' }); };
        li.append(b); if (c.gist) li.append(el('div', 'meta', c.gist)); ul.append(li);
      });
      out.push(d.chapters.length ? ul : el('div', 'wait', t('notes.part.noChapters')));
    }
    if (d.dropped) out.push(el('div', 'note', t('notes.part.dropped', { n: d.dropped })));
    return out;
  }
  function draftSection() {
    const d = full.draft;
    if (!d || full.state === 'done') return [];
    const out = [el('h3', '', t('notes.draft.title')),
      el('div', 'note', t('notes.draft.coverage', { through: d.through, total: full.lines }))];
    const status = d.state === 'run' ? 'working' : d.state === 'error' ? 'retry' : d.state === 'rate' ? 'rate'
      : ['limit', 'unavailable', 'ended', 'legacy', 'unknown', 'empty'].includes(d.state) ? d.state : 'waiting';
    out.push(el('div', status === 'retry' || status === 'unavailable' ? 'bad' : 'wait', t(`notes.draft.${status}`)));
    for (const batch of d.batches || []) {
      out.push(el('div', 'meta', t('notes.draft.range', { from: batch.from, through: batch.through })));
      out.push(el('div', '', batch.data.summary));
      const ul = el('ul');
      for (const [i, point] of (batch.data.points || []).entries()) {
        const li = el('li', '', point.text);
        cited(li, point, `draft-${batch.from}-${i}`);
        const first = point.src?.[0];
        if (first) {
          const jump = el('button', 'cite', t('notes.draft.jump'));
          jump.onclick = () => { hl = [first, first]; renderFull(); body.querySelector(`[data-n="${first}"]`)?.scrollIntoView({ block: 'center' }); };
          li.append(jump);
        }
        ul.append(li);
      }
      if (ul.childNodes.length) out.push(ul);
    }
    return out;
  }
  function renderFull() {
    if (!full || view !== full.id) return;
    const top = body.scrollTop;
    title.textContent = full.room;
    const meta = el('div', 'meta');
    const people = full.people.filter((p) => !p.guest).map((p) => p.name);
    const un = full.people.filter((p) => p.untranscribed).map((p) => p.name), guests = full.people.filter((p) => p.guest).map((p) => p.name);
    const old = full.people.filter((p) => p.old).map((p) => p.name);
    meta.textContent = [full.ended ? t('notes.meta.when', { from: day(full.at), to: hm(full.ended) }) : day(full.at), t('notes.meta.by', { name: full.by }),
      ...(full.stoppedBy ? [t('notes.meta.stoppedBy', { name: full.stoppedBy })] : []), t('notes.meta.people', { names: names(people) || t('notes.meta.nobody') })].join(' · ');
    const out = [meta];
    if (un.length) out.push(el('div', 'note', t('notes.meta.untranscribed', { names: names(un) })));
    if (old.length) out.push(el('div', 'note', t('notes.meta.old', { names: names(old) })));
    if (guests.length) out.push(el('div', 'note', t('notes.meta.guests', { names: names(guests) })));
    if (full.cut) out.push(el('div', 'note', t('notes.meta.cut')));
    if (!o.guest && full.state === 'done') out.push(dmRow(), raftRow());
    out.push(...draftSection());
    for (const [p, n] of PARTS) out.push(...section(p, t(n)));
    out.push(el('h3', '', t('notes.transcript', { n: lines.length })));
    const trBox = el('div', 'tr');
    if (!lines.length) trBox.append(el('div', 'wait', t(full.state === 'rec' ? 'notes.transcript.rec' : 'notes.transcript.none')));
    for (const l of lines) {
      const d = el('div', hl && l.n >= hl[0] && l.n <= hl[1] ? 'hl' : ''); d.dataset.n = l.n;
      d.append(el('span', 't', hm(l.at)), el('span', 'n', saidBy(l.name, l.src === 'chat')), l.text); trBox.append(d);
    }
    out.push(trBox);
    body.replaceChildren(...out); body.scrollTop = top;
  }

  /** 自动发给参会的人: how the Raft DMs went (or why they can't go yet). */
  function dmRow() {
    const row = el('div', 'raft col'), dm = full.dm;
    if (dm?.off) { row.append(el('span', 'wait', t('notes.dm.off'))); return row; }
    if (dm?.wait) { row.append(el('span', 'wait', t('notes.dm.wait'))); return row; }
    if (!dm) { row.append(el('span', 'wait', t('notes.dm.sending'))); return row; }
    if (!dm.sent.length && !dm.failed.length && !dm.status) row.append(el('span', 'wait', t('notes.dm.nobody')));
    if (dm.sent.length) row.append(put(el('span', 'ok'), 'check', t('notes.dm.sent', { names: names(dm.sent) })));
    if (dm.status) row.append(el('span', 'bad', t('notes.dm.failed', { why: botText(dm.status, dm.app) })));
    else for (const f of dm.failed) row.append(el('span', 'bad', t('notes.dm.failedFor', { name: f.name, why: f.code ? botText(f.code, dm.app) : f.why })));
    return row;
  }
  /** 发到 Raft: the notes as one message in a channel, posted by the Deck app; first, whether the app may post at all. */
  function raftRow() {
    const row = el('div', 'raft');
    if (raft && raft.status !== 'ready' && !full.posted) { row.append(el('span', 'bad', t('notes.raft.off', { why: botText(raft.status, raft.app) }))); const b = el('button', '', t('notes.raft.recheck')); b.onclick = () => { raft = null; checkRaft(); renderFull(); }; row.append(b); return row; }
    if (full.posted) { row.append(put(el('span', 'ok'), 'check', t('notes.raft.posted', { name: full.posted.by, channel: full.posted.channel }))); return row; }
    const inp = el('input'); inp.maxLength = 80; inp.placeholder = t('notes.raft.channel'); inp.value = postChannel ?? full.raftDefault ?? '';
    inp.oninput = () => { postChannel = inp.value; };
    for (const ev of ['keydown', 'keyup', 'keypress']) inp.addEventListener(ev, (e) => { e.stopPropagation(); if (ev === 'keydown' && e.key === 'Enter' && !e.isComposing) go.click(); });
    const go = el('button', '', t(posting === full.id ? 'notes.raft.posting' : 'notes.raft.post')); go.disabled = posting === full.id;
    go.title = t('notes.raft.postTip');
    go.onclick = () => {
      const ch = inp.value.trim().replace(/^#/, ''); if (!ch) { o.toast(t('notes.raft.noChannel')); return; }
      const id = full.id; posting = id; o.send({ t: 'notes_post', id, channel: ch }); renderFull();
      setTimeout(() => { if (posting === id) { posting = null; renderFull(); } }, 40000); // no answer (an older office): the button comes back
    };
    row.append(...tParts('notes.raft.to', { channel: inp }), go);
    return row;
  }
  function notice(z, r) {
    if (told.has(r.id)) return;
    told.add(r.id);
    o.toast(t(r.draft ? 'notes.noticeDraft' : 'notes.notice', { name: r.by }), null, 'note');
  }
  function stopNotice(z) {
    const key = `${z}:${stopped[z]}`;
    if (stopped[z] && !stopTold.has(key)) { stopTold.add(key); o.toast(t('notes.stopped.safety'), null, 'note'); }
  }

  return {
    welcome(m) {
      supported = !!m.notesLive; live = m.notesLive || {};
      supportsDraft = m.notesDraft === true; startWhat();
      unavailable = new Set(m.notesUnavailable || []); stopped = m.notesStopped || {};
      if (supported) o.send({ t: 'notes_hi', v: supportsDraft ? 3 : 2 }); // v3 adds the live-draft notice; it includes v2
      const z = o.room(); if (z && unavailable.has(z)) o.toast(t('notes.unavailable'), null, 'note');
      else if (z && live[z]) notice(z, live[z]);
      if (z) stopNotice(z);
    },
    /** May I press 记录 here? (guests only stop) */
    canStart: () => !o.guest,
    /** Does this office do 会议纪要? (an older one: no buttons) */
    supported: () => supported,
    /** The recording in this room now, or null. */
    live: (zone) => zone && unavailable.has(zone) ? { ...(live[zone] || {}), paused: true } : zone && live[zone] ? { ...live[zone], paused: false } : null,
    /** 记录 / 停止记录 in my room. */
    toggle() {
      const z = o.room(); if (!z) return;
      if (unavailable.has(z)) { o.toast(t('notes.unavailable'), null, 'note'); return; }
      if (live[z]) { o.send({ t: 'notes_stop' }); return; }
      raft = null; startStatus(); checkRaft(); sb.classList.add('on'); sGo.focus();
      setTimeout(() => { if (!raft && sb.classList.contains('on')) { sSt.className = 'st bad'; sSt.textContent = t('notes.start.noStatus'); } }, 8000);
    },
    open: () => openList(),
    /** meeting.js: I walked into a room. */
    entered(z) {
      if (unavailable.has(z)) o.toast(t('notes.unavailable'), null, 'note');
      else if (live[z]) notice(z, live[z]);
      stopNotice(z);
    },
    msg(m) {
      if (m.t === 'notes_live') {
        const was = live[m.zone];
        unavailable.delete(m.zone);
        if (m.live) live[m.zone] = m.live; else delete live[m.zone];
        if (m.live) delete stopped[m.zone];
        if (m.reason === 'safety' && m.at) stopped[m.zone] = m.at;
        if (!m.live && was && view === was.id) o.send({ t: 'notes_get', id: was.id });
        if (o.room() !== m.zone) return;
        if (m.reason === 'safety') { stopTold.add(`${m.zone}:${m.at}`); o.toast(t('notes.stopped.safety'), null, 'note'); }
        else if (m.live) { told.add(m.live.id); o.toast(t(m.live.draft ? 'notes.startedDraft' : 'notes.started', { name: m.live.by }), null, 'note'); }
        else if (was && m.empty) o.toast(m.by ? t('notes.stopped.emptyBy', { name: m.by }) : t('notes.stopped.empty'), null, 'note');
        else if (was) o.toast(m.by ? t('notes.stopped.by', { name: m.by }) : t('notes.stopped'), () => openOne(was.id), 'note', t('notes.view'));
      } else if (m.t === 'notes_paused') {
        unavailable.add(m.zone);
        if (o.room() === m.zone) o.toast(t('notes.unavailable'), null, 'note');
      } else if (m.t === 'notes_state') {
        const z = o.room(), wasPaused = z && unavailable.has(z);
        live = m.notesLive || {}; unavailable = new Set(m.notesUnavailable || []); stopped = m.notesStopped || {};
        const hadDraft = supportsDraft;
        supportsDraft = m.notesDraft === true; startWhat();
        if (supported && supportsDraft && !hadDraft) o.send({ t: 'notes_hi', v: 3 });
        if (z && wasPaused && !unavailable.has(z) && live[z]) o.toast(t('notes.resumed'), null, 'note');
        if (z) stopNotice(z);
      } else if (m.t === 'notes_list') renderList(m.items);
      else if (m.t === 'notes_full') { if (view === m.notes.id) { const same = full?.id === m.notes.id; full = m.notes; lines = m.lines; if (!same) { openCites = new Set(); hl = null; } renderFull(); } }
      else if (m.t === 'notes_draft') { if (view === m.id) o.send({ t: 'notes_get', id: m.id }); }
      else if (m.t === 'notes_part') {
        if (full && full.id === m.id) { full.parts[m.part] = { st: m.st, data: m.data, err: m.err }; full.state = PARTS.every(([p]) => ['ok', 'err'].includes(full.parts[p]?.st)) ? 'done' : 'gen'; renderFull(); }
        if (view === 'list') o.send({ t: 'notes_list' });
      } else if (m.t === 'notes_raft') { raft = m; startStatus(); if (full) renderFull(); }
      else if (m.t === 'notes_ready') { // 会议纪要好了: a card per meeting I was in
        for (const it of m.items) toasted.add(it.id);
        const it = m.items[0]; if (!it || view === it.id) return;
        o.toast(m.items.length > 1 ? t('notes.ready.many', { n: m.items.length, room: it.room }) : t('notes.ready.one', { room: it.room }), () => (m.items.length > 1 ? openList() : openOne(it.id)), 'note', t('notes.view'));
      } else if (m.t === 'notes_dm') { if (full?.id === m.id) { full.dm = m.dm; renderFull(); } if (m.dm?.status) o.toast(t('notes.dm.toast', { why: botText(m.dm.status, m.dm.app) }), () => openOne(m.id), 'note', t('notes.view')); }
      else if (m.t === 'notes_posted') {
        if (posting === m.id) posting = null;
        if (m.ok) { if (full?.id === m.id) full.posted = { channel: m.channel, by: m.by }; o.toast(t('notes.raft.postedToast', { channel: m.channel }), null, 'check'); }
        else o.toast(m.code || m.reason ? errText(m) : t('notes.raft.failed'));
        if (full?.id === m.id) renderFull();
      } else if (m.t === 'notes_err') o.toast(errText(m));
    },
  };
}
